import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fsGetCalories,
  fsLogCalorieEntry,
  fsDeleteCalorieEntry,
  fsSetCalorieGoal,
} from '../services/firestoreDb';
import { localDateKey } from '../utils/dateKey';

function getToday() {
  return localDateKey();
}

function storageScope(uid) {
  return uid ? `user_${uid}` : 'guest';
}

function entriesKey(uid) {
  return `sm_calories_${storageScope(uid)}_${getToday()}`;
}

function goalKey(uid) {
  return `sm_calorie_goal_${storageScope(uid)}`;
}

function localGetEntries(uid = null) {
  try {
    const raw = localStorage.getItem(entriesKey(uid)) ??
      (!uid ? localStorage.getItem(`sm_calories_${getToday()}`) : null);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : (parsed.entries ?? []);
  } catch {
    return [];
  }
}

function localSetEntries(entries, uid = null) {
  localStorage.setItem(entriesKey(uid), JSON.stringify(entries));
}

function localGetGoal(uid = null) {
  const raw = localStorage.getItem(goalKey(uid)) ??
    (!uid ? localStorage.getItem('sm_calorie_goal') : null);
  const parsed = Number.parseInt(raw || '2000', 10);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= 20000
    ? parsed
    : 2000;
}

function localSetGoal(g, uid = null) {
  localStorage.setItem(goalKey(uid), String(g));
}

function entryAmount(entry, key) {
  const value = Number(entry?.[key]);
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function useCalories() {
  const { user } = useAuth();
  const uid = user?.id;

  const [entries, setEntriesState] = useState(() => localGetEntries(null));
  const [goal, setGoalState] = useState(() => localGetGoal(null));

  useEffect(() => {
    let active = true;
    const cachedEntries = localGetEntries(uid);
    const cachedGoal = localGetGoal(uid);
    setEntriesState(cachedEntries);
    setGoalState(cachedGoal);

    if (!uid) return () => { active = false; };
    fsGetCalories(uid)
      .then(async (data) => {
        if (!active) return;
        const guestEntries = localGetEntries(null);
        const shouldMigrateGuest = data === null || data.hasDaily === false;
        const seedEntries = cachedEntries.length > 0
          ? cachedEntries
          : guestEntries;

        if (!shouldMigrateGuest) {
          const remoteEntries = Array.isArray(data.entries) ? data.entries : [];
          localSetEntries(remoteEntries, uid);
          setEntriesState(remoteEntries);
        } else if (seedEntries.length > 0) {
          localSetEntries(seedEntries, uid);
          setEntriesState(seedEntries);
          await Promise.all(seedEntries.map((entry) =>
            fsLogCalorieEntry(uid, entry).catch(() => {})
          ));
        }

        if (data?.hasGoal) {
          localSetGoal(data.goal, uid);
          setGoalState(data.goal);
        } else {
          const seedGoal = cachedGoal !== 2000 ? cachedGoal : localGetGoal(null);
          localSetGoal(seedGoal, uid);
          setGoalState(seedGoal);
          await fsSetCalorieGoal(uid, seedGoal).catch(() => {});
        }
      })
      .catch((err) => console.warn('[useCalories] Failed to load from Firestore:', err));
    return () => { active = false; };
  }, [uid]);

  const totalCalories = useMemo(() => entries.reduce((s, e) => s + entryAmount(e, 'calories'), 0), [entries]);
  const totalProtein  = useMemo(() => entries.reduce((s, e) => s + entryAmount(e, 'protein'), 0), [entries]);
  const totalCarbs    = useMemo(() => entries.reduce((s, e) => s + entryAmount(e, 'carbs'), 0), [entries]);
  const totalFat      = useMemo(() => entries.reduce((s, e) => s + entryAmount(e, 'fat'), 0), [entries]);
  const progress      = useMemo(() => (goal > 0 ? totalCalories / goal : 0), [totalCalories, goal]);

  const addEntry = useCallback(
    (entry) => {
      const now = new Date();
      const newEntry = {
        mealType: 'snack',
        quantity: 1,
        ...entry,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        createdAt: now.toISOString(),
        timezoneOffsetMinutes: -now.getTimezoneOffset(),
      };
      setEntriesState((current) => {
        const next = [...current, newEntry];
        localSetEntries(next, uid);
        return next;
      });
      if (uid) fsLogCalorieEntry(uid, newEntry).catch((err) => console.warn('[useCalories] Log entry failed:', err));
    },
    [uid]
  );

  const deleteEntry = useCallback(
    (id) => {
      setEntriesState((current) => {
        const next = current.filter((e) => e.id !== id);
        localSetEntries(next, uid);
        return next;
      });
      if (uid) fsDeleteCalorieEntry(uid, id).catch((err) => console.warn('[useCalories] Delete entry failed:', err));
    },
    [uid]
  );

  const setGoal = useCallback(
    (n) => {
      const val = parseInt(n, 10);
      if (!Number.isFinite(val) || val < 1 || val > 20000) return;
      localSetGoal(val, uid);
      setGoalState(val);
      if (uid) fsSetCalorieGoal(uid, val).catch((err) => console.warn('[useCalories] Set goal failed:', err));
    },
    [uid]
  );

  return {
    entries,
    goal,
    totalCalories,
    totalProtein,
    totalCarbs,
    totalFat,
    progress,
    addEntry,
    deleteEntry,
    setGoal,
  };
}
